import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Badge
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const SectionEditor = () => {
  const { id: sspId, sectionId } = useParams();
  const navigate = useNavigate();
  const [section, setSection] = useState(null);
  const [content, setContent] = useState('');
  const [completion, setCompletion] = useState('draft');
  const [changeNote, setChangeNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  const [statusWarningMessage, setStatusWarningMessage] = useState('');

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saving', 'saved', 'unsaved'
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const initialLoadRef = useRef(true);

  useEffect(() => {
    if (sspId && sectionId) {
      fetchSection();
    }
  }, [sspId, sectionId]);

  useEffect(() => {
    if (section) {
      setContent(section.current_content_html || '');
      setCompletion(section.completion || 'draft');
      setLastSavedAt(section.updatedAt ? new Date(section.updatedAt) : null);
      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);
      initialLoadRef.current = true;
    }
  }, [section]);

  const fetchSection = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(
        `${baseURL}/ssp-studio/ssps/${sspId}/sections`,
        { withCredentials: true }
      );
      const foundSection = response.data.data.find(s => s._id === sectionId);
      if (foundSection) {
        setSection(foundSection);
      } else {
        setError('Section not found');
      }
    } catch (err) {
      console.error('Error fetching section:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  // Auto-save function
  const performAutoSave = async () => {
    if (!hasUnsavedChanges || !section) return;

    setAutoSaveStatus('saving');

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.put(
        `${baseURL}/ssp-studio/ssps/${sspId}/sections/${section._id}`,
        {
          current_content_html: content,
          completion,
          change_note: 'Auto-saved'
        },
        { withCredentials: true }
      );

      setAutoSaveStatus('saved');
      setHasUnsavedChanges(false);
      setLastSavedAt(new Date());
    } catch (err) {
      console.error('Auto-save error:', err);
      setAutoSaveStatus('unsaved');
      // Don't show error alert for auto-save failures, just keep status as unsaved
    }
  };

  // Debounced auto-save effect
  useEffect(() => {
    // Skip auto-save on initial load
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);
    setAutoSaveStatus('unsaved');

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set new timer for auto-save (3 seconds after user stops typing)
    autoSaveTimerRef.current = setTimeout(() => {
      performAutoSave();
    }, 3000);

    // Cleanup timer on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [content, completion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Auto-detect status based on content
  const getAutoDetectedStatus = (contentText) => {
    const stripped = contentText.replace(/<[^>]*>/g, '').trim();

    if (!stripped || stripped.length < 10) {
      return 'empty';
    } else if (stripped.length < 100) {
      return 'draft';
    }
    // If content is substantial, keep current status or suggest draft
    return completion === 'ready' ? 'ready' : 'draft';
  };

  // Validate if section can be marked as "ready"
  const validateReadyStatus = (contentText) => {
    const stripped = contentText.replace(/<[^>]*>/g, '').trim();
    const wordCount = stripped.split(/\s+/).length;

    if (stripped.length < 50) {
      return {
        valid: false,
        message: 'Section content is too short. Add at least 50 characters of meaningful content before marking as "Ready".'
      };
    }

    if (wordCount < 10) {
      return {
        valid: false,
        message: 'Section needs more detail. Add at least 10 words of content before marking as "Ready".'
      };
    }

    return { valid: true };
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Auto-suggest status based on content
    const suggestedStatus = getAutoDetectedStatus(newContent);

    // Only auto-change if currently empty and adding content
    if (completion === 'empty' && suggestedStatus !== 'empty') {
      setCompletion('draft');
    }
    // Or if clearing content
    else if (suggestedStatus === 'empty' && completion !== 'empty') {
      setCompletion('empty');
    }
  };

  const handleCompletionChange = (e) => {
    const newStatus = e.target.value;

    // Validate if trying to mark as "ready"
    if (newStatus === 'ready') {
      const validation = validateReadyStatus(content);
      if (!validation.valid) {
        setShowStatusWarning(true);
        setStatusWarningMessage(validation.message);
        return; // Don't allow status change
      }
    }

    setShowStatusWarning(false);
    setCompletion(newStatus);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      );
      if (!confirmed) return;
    }
    navigate(`/ssp-studio/ssp/${sspId}`);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Final validation before save
    if (completion === 'ready') {
      const validation = validateReadyStatus(content);
      if (!validation.valid) {
        setError(validation.message);
        setShowStatusWarning(true);
        setStatusWarningMessage(validation.message);
        return;
      }
    }

    setLoading(true);
    setError(null);

    // Clear any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.put(
        `${baseURL}/ssp-studio/ssps/${sspId}/sections/${section._id}`,
        {
          current_content_html: content,
          completion,
          change_note: changeNote || 'Content updated'
        },
        { withCredentials: true }
      );

      setSuccess('Section saved successfully!');
      setHasUnsavedChanges(false);
      setAutoSaveStatus('saved');
      setLastSavedAt(new Date());

      setTimeout(() => {
        navigate(`/ssp-studio/ssp/${sspId}`);
      }, 1000);
    } catch (err) {
      console.error('Error saving section:', err);
      setError(err.response?.data?.message || 'Failed to save section');
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Button color="secondary" outline size="sm" onClick={handleCancel}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to SSP
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={9}>
          <Card>
            <CardBody>
              <CardTitle className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0">{section.title_snapshot}</h5>
                    {/* Auto-save status indicator */}
                    <div className="mt-2">
                      {autoSaveStatus === 'saving' && (
                        <small className="text-muted">
                          <span className="spinner-border spinner-border-sm me-1" style={{ width: '12px', height: '12px' }}></span>
                          Saving draft...
                        </small>
                      )}
                      {autoSaveStatus === 'saved' && lastSavedAt && (
                        <small className="text-success">
                          <i className="mdi mdi-check-circle me-1"></i>
                          All changes saved {new Date().getTime() - lastSavedAt.getTime() < 60000
                            ? 'just now'
                            : `at ${lastSavedAt.toLocaleTimeString()}`}
                        </small>
                      )}
                      {autoSaveStatus === 'unsaved' && (
                        <small className="text-warning">
                          <i className="mdi mdi-alert-circle me-1"></i>
                          Unsaved changes
                        </small>
                      )}
                    </div>
                  </div>
                  <Badge color="info">Level {section.level_snapshot}</Badge>
                </div>
              </CardTitle>

              {section.guidance_text_html && (
                <Alert color="info" className="mb-4">
                  <strong>Guidance:</strong>
                  <div dangerouslySetInnerHTML={{ __html: section.guidance_text_html }} />
                </Alert>
              )}

              <Form onSubmit={handleSave}>
                <FormGroup>
                  <Label for="content">Section Content *</Label>
                  <Input
                    type="textarea"
                    name="content"
                    id="content"
                    rows="20"
                    value={content}
                    onChange={handleContentChange}
                    placeholder="Enter section content here..."
                    required
                  />
                  <small className="text-muted">
                    Tip: Use HTML formatting if needed (e.g., &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;)
                  </small>
                  <div className="mt-2">
                    <Badge color={
                      content.replace(/<[^>]*>/g, '').trim().length > 100 ? 'success' :
                      content.replace(/<[^>]*>/g, '').trim().length > 10 ? 'warning' : 'secondary'
                    }>
                      {content.replace(/<[^>]*>/g, '').trim().split(/\s+/).filter(w => w).length} words
                    </Badge>
                    <Badge color="light" className="ms-2 text-dark">
                      {content.replace(/<[^>]*>/g, '').trim().length} characters
                    </Badge>
                  </div>
                </FormGroup>

                {/* Status Warning */}
                {showStatusWarning && (
                  <Alert color="warning" className="mb-3">
                    <div className="d-flex align-items-start">
                      <i className="mdi mdi-alert-circle me-2 mt-1" style={{ fontSize: '1.5em' }}></i>
                      <div>
                        <strong>Section Not Ready</strong>
                        <p className="mb-0 mt-1">{statusWarningMessage}</p>
                        <small className="text-muted d-block mt-2">
                          Sections marked as "Ready" should have sufficient content for review and publication.
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}

                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="completion">Completion Status</Label>
                      <Input
                        type="select"
                        name="completion"
                        id="completion"
                        value={completion}
                        onChange={handleCompletionChange}
                      >
                        <option value="empty">Empty - No content yet</option>
                        <option value="draft">Draft - Work in progress</option>
                        <option value="ready">Ready - Complete and reviewed</option>
                      </Input>
                      <small className="text-muted">
                        Status automatically updates based on content length
                      </small>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="changeNote">Change Note (Optional)</Label>
                      <Input
                        type="text"
                        name="changeNote"
                        id="changeNote"
                        value={changeNote}
                        onChange={(e) => setChangeNote(e.target.value)}
                        placeholder="Brief description of changes"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="mt-4">
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-content-save me-1"></i>
                        Save Section
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    className="ms-2"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col lg={3}>
          <Card>
            <CardBody>
              <CardTitle>Current Status</CardTitle>
              <div className="text-center mb-3">
                <Badge
                  color={
                    completion === 'ready' ? 'success' :
                    completion === 'draft' ? 'warning' : 'secondary'
                  }
                  style={{ fontSize: '1.1em', padding: '8px 16px' }}
                >
                  {completion === 'ready' ? '✓ Ready' :
                   completion === 'draft' ? '• Draft' : '○ Empty'}
                </Badge>
              </div>
              <div className="small text-muted">
                {completion === 'empty' && (
                  <p>Add content to begin working on this section.</p>
                )}
                {completion === 'draft' && (
                  <p>Continue adding content. Mark as "Ready" when complete and reviewed.</p>
                )}
                {completion === 'ready' && (
                  <p>This section is complete and ready for review or publication.</p>
                )}
              </div>
            </CardBody>
          </Card>

          <Card className="mt-3">
            <CardBody>
              <CardTitle>Section Info</CardTitle>

              {/* Auto-save status */}
              <div className="mb-3 pb-3 border-bottom">
                <small className="text-muted d-block mb-1">Auto-save Status</small>
                {autoSaveStatus === 'saving' && (
                  <Badge color="info">
                    <span className="spinner-border spinner-border-sm me-1" style={{ width: '10px', height: '10px' }}></span>
                    Saving...
                  </Badge>
                )}
                {autoSaveStatus === 'saved' && (
                  <Badge color="success">
                    <i className="mdi mdi-check-circle me-1"></i>
                    Saved
                  </Badge>
                )}
                {autoSaveStatus === 'unsaved' && (
                  <Badge color="warning">
                    <i className="mdi mdi-alert-circle me-1"></i>
                    Unsaved
                  </Badge>
                )}
                {lastSavedAt && autoSaveStatus === 'saved' && (
                  <div className="mt-1">
                    <small className="text-muted">
                      {new Date().getTime() - lastSavedAt.getTime() < 60000
                        ? 'Just now'
                        : lastSavedAt.toLocaleTimeString()}
                    </small>
                  </div>
                )}
              </div>

              <div className="mb-3">
                <small className="text-muted d-block">Order</small>
                <strong>#{section.order_index_snapshot}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Level</small>
                <strong>{section.level_snapshot}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Last Updated</small>
                <strong>
                  {section.updatedAt ? new Date(section.updatedAt).toLocaleDateString() : 'Never'}
                </strong>
              </div>
              {section.version_history && section.version_history.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted d-block">Version History</small>
                  <strong>{section.version_history.length} versions</strong>
                </div>
              )}
            </CardBody>
          </Card>

          {content && (
            <Card className="mt-3">
              <CardBody>
                <CardTitle>Preview</CardTitle>
                <div
                  className="border p-2"
                  style={{ maxHeight: '400px', overflow: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default SectionEditor;
